import dayjs from 'dayjs'
import { uniqueId } from 'lodash'
import { Fragment, useEffect, useReducer, useRef, useState } from 'react'
import { useDispatch } from 'react-redux'

import { useReportModal } from 'components/Modal/ReportModal/ReportModal'
import { useResponsive, useUploadImage } from 'hooks'
import {
  Attachment,
  ConferenceCall,
  CreateMessage,
  Message,
  User,
} from 'lib/_types'
import {
  chatRoomToChatRoomContextSchema,
  logEvent,
  snowplowEvent,
  userToUserContextSchemaData,
} from 'lib/analytics'
import {
  useCreateMessageMutation,
  useDeleteChatRoomMessagesMutation,
  useGetChatRoomQuery,
} from 'store/api/yay/chat'
import { useCurrentUser } from 'store/api/yay/user'
import { setIsMeaningfulTime } from 'store/actions/uiState'

import LightBox from 'components/LightBox'

import ChatReplyForm from './ChatReplyForm'
import { ChatReplyFormPreview } from './ChatReplyFormPreview'
import ChatRoomHeader from './ChatRoomHeader'
import ChatWindow from './ChatWindow'
import RequestToChatOptionPanel from './RequestToChatOptionPanel'
import { useFetchMessages } from './hooks'

import './style.scss'

const ChatRoom = ({
  id,
  onMenuClicked,
  onClose,
}: {
  id: number
  onMenuClicked: Function
  onClose: Function
}) => {
  const dispatch = useDispatch()
  const { isMobile } = useResponsive()
  const { upload } = useUploadImage()

  const chatRoomRef = useRef<HTMLDivElement>()

  const { data: currentUser } = useCurrentUser()

  const chatRoom = useGetChatRoomQuery({ chatId: id })
  const chatId = chatRoom?.data?.id

  const {
    __messages,
    __messagesSet,
    messagesRef,
    isFetching,
    __readyTimestamp,

    __toMessageId,
    __toMessageIdSet,
    isFetchingTop,
    hasMoreTop,

    __fromMessageIdSet,
    isFetchingBottom,
    hasMoreBottom,

    __aroundMessageIdSet,
    isExchanged,
  } = useFetchMessages({ chatId })

  const [__replyMessage, __replyMessageSet] = useState<Message>()
  const [__replyMessageOwner, __replyMessageOwnerSet] = useState<User>()
  const [__focusId, __focusIdSet] = useState(0)

  // TODO: move this to another file?
  const [previewState, previewDispatch] = useReducer(
    (_state: any, action: any) => {
      switch (action.type) {
        case 'active':
          return {
            isActive: true,
            url: action.payload.url,
            type: action.payload.type,
          }
        case 'inactive':
          return {
            isActive: false,
            url: null,
            type: null,
          }
      }
    },
    {
      isActive: false,
      url: null,
      type: null,
    }
  )

  const [createMessage] = useCreateMessageMutation()
  const [deleteMessage] = useDeleteChatRoomMessagesMutation()
  const reportModal = useReportModal()

  const opponentUser = chatRoom?.data?.members?.find?.(
    (x) => x?.id != currentUser?.id
  )

  const onSendMessage = async ({
    messageType,
    text,
    file,
    sticker_pack_id,
    sticker_id,
    sticker,
    gif_image_id,
    gif,
    call_type,
  }: CreateMessage) => {
    const _id = Number(uniqueId('0.'))

    // The point of `_tempMessage` is that we have something
    // to display while the actual image is being uploaded.
    let _tempMessage: Partial<Message> = {
      _sending: true,
      // We'll need a number as temporary ID that doesn't intefere
      // with existing IDs, hence the fraction.
      id: _id,
      created_at: Math.floor(+dayjs() / 1000),
      user_id: currentUser?.id,
      message_type: messageType,
      text,
      sticker,
      gif,
      ...(['call', 'individual_call'].includes(messageType)
        ? {
            conference_call: {
              active: true,
              call_type,
            } as ConferenceCall,
          }
        : {}),
    }

    if (file) {
      _tempMessage = {
        ..._tempMessage,
        attachment: URL.createObjectURL(file),
      }
    }

    __messagesSet((prevState) => {
      return [...prevState, _tempMessage as Message]
    })

    let createMessageRes: any

    // Now, we get an actual ID from BE.
    if (file) {
      let resUpload: Array<Attachment>

      try {
        resUpload = await upload([file], 'chat_message')
      } catch {
        __messagesSet((prevState) => {
          return prevState?.filter?.((x) => x?.id != _id)
        })

        return
      }

      createMessageRes = await createMessage({
        chatId: id?.toString?.(),
        data: {
          message_type: messageType,
          attachment_file_name: resUpload?.[0]?.fileName,
          parent_id: __replyMessage?.id,
        },
      })
    } else {
      createMessageRes = await createMessage({
        chatId: id?.toString?.(),
        data: {
          message_type: messageType,
          text,
          sticker_pack_id,
          sticker_id,
          gif_image_id,
          call_type,
          parent_id: __replyMessage?.id,
        },
      })
    }

    if (createMessageRes?.['data']?.id) {
      const messageMap = {
        text: 'text',
        image: 'image',
        eternal_image: 'image',
        video: 'video',
        eternal_video: 'video',
        gif: 'gif',
        sticker: 'sticker',
      } as const

      snowplowEvent.send_message({
        type: messageMap[messageType] || 'system_message',
      })

      const isCall = ['call', 'individual_call'].includes(messageType)

      if (isCall) {
        snowplowEvent.create_chat_call({
          user: userToUserContextSchemaData({ user: opponentUser }),
        })
      } else {
        logEvent.chat_message_send({
          type: (['image', 'eternal_image'].includes(messageType)
            ? 'image'
            : messageType) as any,
          target_chat_room_id: id?.toString?.(),
          target_user_id: chatRoom?.data?.is_group
            ? undefined
            : opponentUser?.id?.toString?.(),
        })
      }
    }

    __messagesSet((prevState) => {
      return prevState?.map?.((x) => {
        // We then replace `_tempId` with actual ID. This shall be later
        // cleaned up by `uniqBy` of `[messagesPreviousRes, messagesLatestRes]`'s `useEffect`,
        // replacing it with the true data from BE. Then unmark it from sending state.
        if (x?.id == _tempMessage.id) {
          return {
            ...x,
            _sending: false,
            id: createMessageRes?.['data']?.id,
            ...(__replyMessage && {
              parent_id: __replyMessage.id,
              parent: __replyMessage,
            }),
          }
        }

        return x
      })
    })

    onResetReply()

    return createMessageRes
  }

  const onDeleteMessage = async (messageId: number) => {
    const res = await deleteMessage({
      chatId: id,
      messageId: messageId,
    })

    if (res?.['data']?.result == 'success') {
      __messagesSet((prev) =>
        prev.map((m) =>
          m.id === messageId
            ? { ...m, message_type: 'deleted' } // mark current message as deleted
            : m.parent_id === messageId
            ? { ...m, parent: { ...m.parent, message_type: 'deleted' } } // mark all mentioned's parent as deleted
            : m
        )
      )

      __replyMessageSet((prev) => {
        if (messageId !== prev?.id) return prev
      })
    }
  }

  const onReport = (userId: number) => {
    reportModal.open({
      chatRoomId: id.toString(),
      userId: userId.toString(),
    })
  }

  const onReply = (message: Message) => {
    if (!message) {
      onResetReply()
      return
    }

    const owner = chatRoom.data.members.find((u) => u.id === message.user_id)
    __replyMessageOwnerSet(owner)
    __replyMessageSet(message)
  }

  const onResetReply = () => {
    __replyMessageOwnerSet(undefined)
    __replyMessageSet(undefined)
  }

  const onResetAroundMessage = () => {
    __aroundMessageIdSet(0)
    __toMessageIdSet(0)
    __fromMessageIdSet(0)
  }

  const onJumpToMessage = (id: number) => {
    if (!id) return

    const target = messagesRef.current?.find((m) => m.id === id)

    __focusIdSet(id)

    if (target) {
      setTimeout(() => {
        __focusIdSet(0)
      }, 200)
    } else {
      __aroundMessageIdSet(id)
      __toMessageIdSet(0)
      __fromMessageIdSet(0)
    }
  }

  useEffect(() => {
    setTimeout(() => {
      __focusIdSet(0)
    }, 200)
  }, [__readyTimestamp])

  // mobile window resize event
  useEffect(() => {
    function resizeHeight() {
      if (chatRoomRef?.current) {
        chatRoomRef.current.style.height = `${window.innerHeight}px`
      }
    }

    if (isMobile) {
      window.addEventListener('resize', resizeHeight)
    }

    return () => {
      window.removeEventListener('resize', resizeHeight)
    }
  }, [isMobile])

  // snowplow
  useEffect(() => {
    if (!chatRoom.isSuccess || !chatRoom.data) return

    snowplowEvent.open_chat({
      data: {
        chat_id: id,
        is_owner: chatRoom.data.members?.[0]?.id === currentUser.id,
        unread_count: chatRoom.data.unread_count,
      },
      user: userToUserContextSchemaData({
        user: chatRoom.data.members?.find((m) => m.id !== currentUser.id),
      }),
      chatRoom: chatRoomToChatRoomContextSchema({ chatRoom: chatRoom.data }),
    })
  }, [chatRoom.isSuccess])

  // count meaningful time
  useEffect(() => {
    dispatch(setIsMeaningfulTime({ state: true }))

    return () => {
      dispatch(setIsMeaningfulTime({ state: false }))
    }
  }, [])

  const isPendingChat = chatRoom?.data?.is_request

  return (
    <Fragment>
      <div className="ChatRoom" ref={chatRoomRef}>
        <ChatRoomHeader
          onMenuClicked={onMenuClicked}
          onClose={onClose}
          chatRoom={chatRoom}
          disableCallFeature={isPendingChat}
          isExchanged={isExchanged}
          onSendMessage={onSendMessage}
        />

        <ChatWindow
          // trigger rerender when key changed
          key={__readyTimestamp}
          chatRoom={chatRoom?.data}
          messages={__messages}
          isFetching={isFetching}
          focusId={__focusId}
          // event function
          onPreviewMedia={(payload) => {
            previewDispatch({ type: 'active', payload })
          }}
          onDeleteMessage={onDeleteMessage}
          onReport={onReport}
          onReply={onReply}
          onJumpToMessage={onJumpToMessage}
          onResetAroundMessage={onResetAroundMessage}
          // top fetching props
          toMessageId={__toMessageId}
          toMessageIdSet={__toMessageIdSet}
          isFetchingTop={isFetchingTop}
          hasMoreTop={hasMoreTop}
          // bottom fetching props
          fromMessageIdSet={__fromMessageIdSet}
          isFetchingBottom={isFetchingBottom}
          hasMoreBottom={hasMoreBottom}
        />

        {isPendingChat ? (
          <RequestToChatOptionPanel
            chatRoom={chatRoom?.data}
            opponentUser={opponentUser}
          />
        ) : (
          <>
            {__replyMessage && (
              <ChatReplyFormPreview
                message={__replyMessage}
                messageOwner={__replyMessageOwner}
                onResetReply={onResetReply}
                onJumpToMessage={onJumpToMessage}
              />
            )}
            <ChatReplyForm
              chatRoomId={id}
              onSendMessage={onSendMessage}
              replyMessageId={__replyMessage?.id}
            />
          </>
        )}
      </div>

      <LightBox
        isActive={previewState.isActive}
        onClose={() => {
          previewDispatch({ type: 'inactive' })
        }}
        url={previewState.url}
        type={previewState.type}
      />
    </Fragment>
  )
}

export default ChatRoom
